/**
 * The gate in front of every screen belonging to an installable app.
 *
 * It answers before the page mounts, so an uninstalled app issues no queries
 * that the server would refuse anyway. What it renders in that case is not an
 * error — it is the app's own intro, which explains what the section is for
 * and offers to install it.
 *
 * The menu is deliberately left alone. Every entry stays visible whether or
 * not its app is installed, so the ERP always looks like the whole product and
 * clicking into a part the company does not have explains itself rather than
 * vanishing. A menu that silently loses entries teaches people the software is
 * broken; one that explains teaches them what it does.
 */
import type { ReactNode } from "react";

import { useSession } from "@/lib/session/use-session";
import type { AppCode } from "@/lib/modules";

import { AppOverviewPage } from "./pages/app-overview-page";

export interface ModuleBoundaryProps {
  app: AppCode;
  children: ReactNode;
  onNavigate?: (path: string) => void;
  onOpenApps?: () => void;
}

export function ModuleBoundary({ app, children, onNavigate, onOpenApps }: ModuleBoundaryProps) {
  const { hasModule, isLoading } = useSession();

  // Nothing is decided until the session says so. Rendering the intro during
  // the bootstrap would flash "not installed" at somebody who has the app.
  if (isLoading) return null;
  if (hasModule(app)) return <>{children}</>;

  return <AppOverviewPage code={app} onNavigate={onNavigate} onOpenApps={onOpenApps} />;
}
